/**
 * Tacit CRM Type Definitions
 * Type-safe interfaces for contact management and AI-powered updates
 * SOTA 2026 - Dark Professional Design System
 */

// ═══════════════════════════════════════════════════════════════════════════
// ENUMS & UNIONS
// ═══════════════════════════════════════════════════════════════════════════

export type ContactStatus = 
  | 'nurture' 
  | 're_engagement' 
  | 'referral' 
  | 'hot_lead' 
  | 'warm_lead' 
  | 'transition' 
  | 'closed_won'
  | 'closed_lost'   
  | 'pending';

export type ActionState = 
  | 'action_required'     // You need to do something
  | 'waiting_response'    // Ball is in their court
  | 'response_received'   // They replied, you need to process
  | 'scheduled'           // Meeting/call scheduled
  | 'on_hold'             // Paused intentionally
  | 'closed_won'          // Deal closed successfully
  | 'closed_lost'         // Lost opportunity
  | 'archived';           // No longer relevant

export type ActionType = 
  | 'outreach'
  | 'follow_up'
  | 'meeting'
  | 'call'
  | 'note'
  | 'response_received'
  | 'status_changed';

export type ActionOutcome = 'positive' | 'neutral' | 'negative';

export type CompanySize = 'startup' | 'smb' | 'mid_market' | 'enterprise';
export type PreferredContactMethod = 'email' | 'phone' | 'linkedin';

// ═══════════════════════════════════════════════════════════════════════════
// CORE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

export interface Contact {
  id: string;
  
  // Basic info
  name: string;
  company: string;
  role?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  
  // AI-extractable structured fields
  location?: string;
  timezone?: string;
  company_size?: CompanySize;
  decision_maker?: boolean;
  preferred_contact_method?: PreferredContactMethod;
  ai_metadata?: Record<string, unknown>;
  
  // CRM fields
  status: ContactStatus;
  pain_point?: string;
  next_action?: string;
  strategy?: string;
  
  // Workflow state
  action_state?: ActionState;
  action_state_changed_at?: string;
  
  // Owner assignment
  assigned_to?: string; // User ID of owner
  
  // Revenue tracking
  deal_value?: number;
  deal_currency?: string;
  deal_closed_at?: string;
  
  // Dates
  due_date?: string; // ISO date string
  completed_at?: string; // ISO timestamp
  last_contact_date?: string; // ISO date string
  
  // Notes
  notes?: string;
  raw_input?: string;
  
  // Multi-tenancy
  organization_id: string;
  
  // Intake provenance. Matches crm_contacts_external_source_allowlist, which
  // migration 20260730180000 tightened from a CHECK that accepted anything.
  external_contact_id?: string;
  external_source?: 'heyreach' | 'manual' | 'trigify' | 'csv_import';
  
  // Metadata
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface Activity {
  id: string;
  contact_id: string;
  organization_id: string;
  // 'message_sent' records a reply the operator sent by hand, on any channel —
  // the one thing the HeyReach webhook could never see.
  activity_type: 'email_sent' | 'message_sent' | 'meeting' | 'call' | 'note_added' | 'status_changed';
  description?: string;
  metadata?: Record<string, any>;
  created_at: string;
  created_by?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// AI UPDATE TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface AIUpdateRequest {
  raw_text: string;
  contact_id?: string;
}

export interface AIFieldUpdate {
  field: string;
  current_value: string | null;
  proposed_value: string;
  confidence: number;
  reasoning: string;
  action: 'add' | 'update' | 'append';
}

export interface AIUpdateResponse {
  matched_contact?: {
    id: string;
    name: string;
    company: string;
    confidence: number;
  };
  proposed_updates: AIFieldUpdate[];
  detected_action_type?: string;
  overall_confidence: number;
}

export interface AIProcessResponse {
  name: string;
  company: string;
  role?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  pain_point?: string;
  next_action?: string;
  strategy?: string;
  suggested_due_date?: string;
  status?: ContactStatus;
  confidence: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// QUERY FILTERS
// ═══════════════════════════════════════════════════════════════════════════

export interface ContactFilters {
  status?: ContactStatus;
  search?: string;
  overdue?: boolean;
  action_state?: ActionState;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS & HELPERS
// ═══════════════════════════════════════════════════════════════════════════

export const STATUS_LABELS: Record<ContactStatus, string> = {
  hot_lead: 'Hot Lead',
  warm_lead: 'Warm Lead',
  nurture: 'Nurture',
  re_engagement: 'Re-engagement',
  referral: 'Referral',
  transition: 'Transition',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  pending: 'Pending',
};

export const STATUS_COLORS: Record<ContactStatus, string> = {
  hot_lead: 'bg-status-danger/10 text-status-danger border-status-danger/30',
  warm_lead: 'bg-status-warning/10 text-status-warning border-status-warning/30',
  nurture: 'bg-status-info/10 text-status-info border-status-info/30',
  re_engagement: 'bg-status-info/10 text-status-info border-status-info/30',
  referral: 'bg-status-success/10 text-status-success border-status-success/30',
  transition: 'bg-status-warning/10 text-status-warning border-status-warning/30',
  closed_won: 'bg-status-success/10 text-status-success border-status-success/30',
  closed_lost: 'bg-surface text-muted-foreground border-border',
  pending: 'bg-surface text-muted-foreground border-border',
};

export const ACTION_STATE_LABELS: Record<ActionState, string> = {
  action_required: 'Action Required',
  waiting_response: 'Waiting for Response',
  response_received: 'Response Received',
  scheduled: 'Scheduled',
  on_hold: 'On Hold',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  archived: 'Archived',
};

export const ACTION_STATE_COLORS: Record<ActionState, string> = {
  action_required: 'bg-status-danger/10 text-status-danger border-status-danger/30',
  waiting_response: 'bg-status-info/10 text-status-info border-status-info/30',
  response_received: 'bg-status-success/10 text-status-success border-status-success/30',
  scheduled: 'bg-status-info/10 text-status-info border-status-info/30',
  on_hold: 'bg-surface text-muted-foreground border-border',
  closed_won: 'bg-status-success/10 text-status-success border-status-success/30',
  closed_lost: 'bg-surface text-muted-foreground border-border',
  archived: 'bg-surface text-text-tertiary border-border',
};

// Helper functions
export function getStatusLabel(status: ContactStatus): string {
  return STATUS_LABELS[status] || status;
}

export function getStatusColor(status: ContactStatus): string {
  return STATUS_COLORS[status] || STATUS_COLORS.pending;
}

export function getActionStateLabel(state: ActionState): string {
  return ACTION_STATE_LABELS[state] || state;
}

export function getActionStateColor(state: ActionState): string {
  return ACTION_STATE_COLORS[state] || ACTION_STATE_COLORS.action_required;
}
